import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Maximize.scss';

/**
 * Maximize / minimize state for panels that must not be remounted when they
 * expand (media playback, rendered PDFs). Callers apply their own expanded
 * class to the existing node and render `backdrop`, so the DOM is only
 * restyled — never moved into a different tree.
 *
 * Handles Escape to minimize and locks page scroll while expanded.
 * `onBeforeChange` runs synchronously before the state update (button, Escape,
 * and backdrop) so callers can snapshot layout while the current size is still
 * on screen.
 */
export function useMaximize({
  minimizeLabel = 'Minimize',
  onBeforeChange,
} = {}) {
  const [expanded, setExpanded] = useState(false);
  const expandedRef = useRef(false);
  const onBeforeChangeRef = useRef(onBeforeChange);
  expandedRef.current = expanded;
  onBeforeChangeRef.current = onBeforeChange;

  const changeExpanded = useCallback((next) => {
    if (next === expandedRef.current) {
      return;
    }

    onBeforeChangeRef.current?.(next);
    expandedRef.current = next;
    setExpanded(next);
  }, []);

  const minimize = useCallback(() => changeExpanded(false), [changeExpanded]);
  const toggleExpanded = useCallback(
    () => changeExpanded(!expandedRef.current),
    [changeExpanded],
  );

  useEffect(() => {
    if (!expanded) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        changeExpanded(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [expanded, changeExpanded]);

  const backdrop = expanded
    ? createPortal(
      <button
        type="button"
        className="ds-maximize-backdrop"
        aria-label={minimizeLabel}
        onClick={minimize}
      />,
      document.body,
    )
    : null;

  return {
    expanded,
    setExpanded: changeExpanded,
    toggleExpanded,
    minimize,
    backdrop,
  };
}

export default useMaximize;
